/**
 * Лендинг justkiwi.ru — точка входа.
 *
 * Весь текст страницы лежит в index.html: его видят поисковики и посетители без JS. Скрипт
 * его оживляет: шапка сайта (как в кабинете и вики), архипелаг режимов с камео SPY_me,
 * копирование адреса сервера, живой онлайн по режимам, привилегии с ценами из магазина
 * кабинета и значки режимов из блоков.
 */
import { api, el, siteHeader, toast } from "../ui.js";
import { cubeIcon } from "../voxel.js";
import { mountArchipelago } from "./archipelago.js";
import { formatRub, onlineByMode, periodLabel, plural, privilegeCards, privilegeTerms } from "./content.js";

/** Разделы страницы в шапке (видны на широком экране). */
const SECTION_LINKS = [
  { label: "Режимы", href: "#modes" },
  { label: "Как начать", href: "#start" },
  { label: "Привилегии", href: "#privileges" },
];

/** Ключ сессии: архипелаг собирается из всплывающих островов только при первом заходе. */
const INTRO_KEY = "jk_landing_intro_played";

/** Первый ли это заход за сессию (и отметить, что всплытие уже показано). */
function claimIntro() {
  try {
    if (sessionStorage.getItem(INTRO_KEY)) return false;
    sessionStorage.setItem(INTRO_KEY, "1");
    return true;
  } catch (storageError) {
    return false;
  }
}

/** Шапка сайта: вход или профиль, тема, уведомления — как в кабинете и вики. */
async function mountHeader() {
  const slot = document.getElementById("site-header");
  const response = await api.get("/api/auth/session");
  const session = response.ok && response.data ? response.data : { authenticated: false };
  let profile = null;
  if (session.authenticated) {
    const me = await api.get("/api/me");
    profile = me.ok ? me.data : null;
  }
  slot.replaceWith(siteHeader({ current: "главная", session, profile, links: SECTION_LINKS }));
}

/**
 * Блок адреса сервера: по нажатию копирует адрес и на пару секунд сам превращается
 * в «Скопировано» (зелёная заливка, галочка), затем возвращается. Размер блока при этом
 * не меняется: обе надписи лежат в одной ячейке сетки. Экранный диктор слышит
 * «Адрес скопирован» через общий статус страницы.
 */
function bindAddressCopy(button) {
  let timer = 0;
  const status = document.getElementById("copy-status");
  button.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(button.dataset.copy);
    } catch (error) {
      toast("Не удалось скопировать — выделите адрес вручную", "error");
      return;
    }
    clearTimeout(timer);
    button.classList.remove("is-copied");
    // Перезапуск анимации, если нажали снова, пока висит «Скопировано».
    void button.offsetWidth;
    button.classList.add("is-copied");
    if (status) status.textContent = "Адрес сервера скопирован";
    timer = setTimeout(() => {
      button.classList.remove("is-copied");
      if (status) status.textContent = "";
    }, 2200);
  });
}

/** Онлайн: плашка над заголовком (только когда кто-то играет), счётчики режимов и подписи островов. */
async function loadOnline(archipelago) {
  const response = await api.get("/api/v1/online");
  if (!response.ok || !response.data) return;
  const total = Math.max(0, Number(response.data.total) || 0);
  const badge = document.getElementById("online-badge");
  if (badge && total > 0) {
    badge.querySelector("[data-online-total]").textContent = `${total} ${plural(total, "игрок", "игрока", "игроков")} онлайн`;
    badge.hidden = false;
  }
  const byMode = onlineByMode(response.data.servers);
  archipelago.setOnline(byMode);
  for (const node of document.querySelectorAll("[data-online-mode]")) {
    const count = byMode[node.dataset.onlineMode] || 0;
    node.textContent = `${count} ${plural(count, "играет", "играют", "играют")}`;
    node.hidden = count === 0;
  }
}

/** Карточка привилегии: имя цветом группы, цена за срок и покупка в магазине кабинета. */
function privilegeCard(card) {
  return el("article", { class: "privilege-card", style: card.color ? `--privilege:${card.color}` : null }, [
    el("h3", { class: "privilege-card__name font-display" }, [
      card.name,
      card.plus ? el("span", { class: "privilege-card__plus" }, "+") : null,
    ]),
    el("p", { class: "privilege-card__price" }, [
      el("span", { class: "font-display" }, formatRub(card.price)),
      el("span", { class: "privilege-card__currency" }, "₽"),
    ]),
    el("p", { class: "privilege-card__period" }, periodLabel(card.days)),
    el("a", {
      href: "/cabinet/#/balance",
      class: "jk-btn jk-btn--default privilege-card__buy",
      "aria-label": `Купить ${card.name}${card.plus ? "+" : ""} в личном кабинете`,
    }, "Купить"),
  ]);
}

/** Подпись срока в переключателе: «3 месяца −16 %», при разных скидках — «до −16 %». */
function termButtonLabel(term) {
  if (!term.discount) return term.label;
  return [term.label, el("span", { class: "privilege-terms__discount" }, `${term.same ? "" : "до "}−${term.discount} %`)];
}

/**
 * Привилегии из магазина кабинета — те же цены, по которым покупают. Над карточками —
 * переключатель срока (месяц, 3 месяца, полгода) со скидкой против помесячной оплаты; он
 * меняет цены карточек. Если магазин не ответил, остаётся текст из разметки со ссылкой на магазин.
 */
async function loadPrivileges() {
  const grid = document.getElementById("privilege-grid");
  const response = await api.get("/api/v1/store/items");
  const items = response.ok ? response.data : [];
  const terms = privilegeTerms(items);
  if (!grid || !terms.length) return;
  let selected = terms[0].days;
  const buttons = terms.map((term) => el("button", {
    type: "button",
    class: "privilege-terms__option",
    "aria-pressed": String(term.days === selected),
    onclick: () => select(term.days),
  }, termButtonLabel(term)));
  const render = () => grid.replaceChildren(...privilegeCards(items, selected).map(privilegeCard));
  function select(days) {
    selected = days;
    buttons.forEach((button, index) => button.setAttribute("aria-pressed", String(terms[index].days === days)));
    render();
  }
  if (terms.length > 1) {
    grid.before(el("div", { class: "privilege-terms", role: "group", "aria-label": "Срок привилегии" }, buttons));
  }
  render();
  grid.classList.add("is-filled");
}

/** Значки режимов из одного блока — тех же, из которых сложены островки архипелага. */
function mountModeIcons() {
  for (const slot of document.querySelectorAll("[data-cube]")) slot.replaceChildren(cubeIcon(slot.dataset.cube));
}

function start() {
  mountHeader();
  for (const button of document.querySelectorAll("[data-copy]")) bindAddressCopy(button);
  mountModeIcons();

  const archipelago = mountArchipelago(document.getElementById("archipelago"), { intro: claimIntro() });
  api.get("/api/v1/players/SPY_me/skin").then((response) => {
    if (response.ok && response.data && response.data.textureHash) archipelago.setSkin(response.data.textureHash);
  });
  loadOnline(archipelago);
  loadPrivileges();
}

start();
